import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import _ from 'lodash';
import { Card, FormHelperText, IconButton } from '@material-ui/core';

import { Edit, HighlightOff } from '@material-ui/icons';
import { Button, FormTextField, OffenderTypeTextField } from '../../atoms';
import { onboardIndividualValidation } from '../../../validators';
import {
  ErrorObject,
  BaseFormProps,
  OnboardIndividualProps,
  FamilyMemberProps,
} from '../../../types';
import { OffenderIdentifierType, OffenderIdentifierTypeDescription } from '../../../enums';

export const OnboardIndividualForm: React.FC<BaseFormProps<OnboardIndividualProps>> = ({
  initialValues,
  submitForm,
  onSuccess,
  onFailure,
}) => {
  const [generalError, setGeneralError] = useState(null);
  const [hasFriendsAndFamily, toggleFriendsAndFamily] = useState(false);
  const names = ['firstName', 'lastName'];
  const placeholders = ['First Name', 'Last Name'];
  const icons = [
    <img src="assets/icons/user-icon.png" alt="userFirst" />,
    <img src="assets/icons/user-icon.png" alt="userLast" />,
    <img src="assets/icons/id-card-icon.png" alt="idNumber" />,
  ];
  const individualProfileTypes = [OffenderIdentifierType.SaIdNumber, OffenderIdentifierType.Trn];

  const _handleFormSubmitError = (
    error: ErrorObject,
    actions: FormikHelpers<OnboardIndividualProps>,
  ) => {
    actions.setSubmitting(false);
    setGeneralError(error.detail);
  };

  const _handleSubmission = (
    formData: OnboardIndividualProps,
    actions: FormikHelpers<OnboardIndividualProps>,
  ) => {
    formData.familyMembers = formData.familyMembers.filter(
      (fm) => fm && !_.isEqual(Object.values(fm), ['', '', '', OffenderIdentifierType.SaIdNumber]),
    );

    const request = _.cloneDeep(formData);

    submitForm(request)
      .then(() => {
        actions.setSubmitting(false);
        onSuccess();
      })
      .catch((error: ErrorObject) => {
        _handleFormSubmitError(error, actions);
        onFailure();
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={onboardIndividualValidation}
      validateOnBlur
      validateOnChange={false}
      onSubmit={_handleSubmission}
      enableReinitialize
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleReset,
        setFieldValue,
      }) => {
        const _addMember = () => {
          const familyMember = values.familyMembers[values.familyMembers.length - 1];
          const isValid = Object.keys(errors?.familyMembers ?? {}).length === 0;

          if (
            !_.isEmpty(familyMember?.firstName) &&
            !_.isEmpty(familyMember?.lastName) &&
            !_.isEmpty(familyMember?.idNumber) &&
            isValid
          ) {
            setFieldValue('familyMembers', [
              ...values.familyMembers,
              {
                firstName: '',
                lastName: '',
                idNumber: '',
                identifierType: OffenderIdentifierType.SaIdNumber,
              } as FamilyMemberProps,
            ]);
          }
        };

        const _addFriendsAndFamily = () => {
          setFieldValue('familyMembers', [
            ...values.familyMembers,
            {
              firstName: '',
              lastName: '',
              idNumber: '',
              identifierType: OffenderIdentifierType.SaIdNumber,
            } as FamilyMemberProps,
          ]);
          toggleFriendsAndFamily(true);
        };

        const _editMember = (index: number) => () => {
          const isValid = Object.keys(errors?.familyMembers ?? {}).length === 0;
          if (!isValid) return;

          const familyMembers = _.filter(_.cloneDeep(values.familyMembers), (member) => {
            if (
              !(
                _.isEmpty(member?.firstName) &&
                _.isEmpty(member?.lastName) &&
                _.isEmpty(member?.lastName)
              )
            ) {
              return true;
            }
            return false;
          });

          familyMembers.push(familyMembers.splice(index, 1)[0]);

          setFieldValue('familyMembers', familyMembers);
          toggleFriendsAndFamily(true);
        };

        const _removeMember = (index: number) => () => {
          const myFamilyMembers = _.cloneDeep(values.familyMembers);
          myFamilyMembers.splice(index, 1);
          setFieldValue('familyMembers', myFamilyMembers);
        };

        const lastMemberIndex = values.familyMembers.length - 1;

        const renderErrorMessage = (index, type) => {
          if (_.isEmpty(errors?.familyMembers)) {
            return null;
          }
          if (_.get(errors.familyMembers[index], 'firstName', false) && type === 'name') {
            return (
              <p className="text-xs font-normal text-red-500">
                {_.get(errors.familyMembers[index], 'firstName', '')}
              </p>
            );
          }
          if (_.get(errors.familyMembers[index], 'lastName', false) && type === 'name') {
            return (
              <p className="text-xs font-normal text-red-500">
                {_.get(errors.familyMembers[index], 'lastName', '')}
              </p>
            );
          }
          if (_.get(errors.familyMembers[index], 'idNumber', false) && type === 'id') {
            return (
              <p className="text-xs font-normal text-red-500">
                {_.get(errors.familyMembers[index], 'idNumber', '')}
              </p>
            );
          }
          return null;
        };

        const _renderFamilyMembers = () => (
          <div className="grid gap-2">
            {_.map(names, (name, itemIndex) => {
              return (
                <FormTextField
                  key={`familyMember-${itemIndex}`}
                  variableName={`familyMembers.${lastMemberIndex}.${name}`}
                  placeholder={placeholders[itemIndex]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                  icon={icons[itemIndex]}
                  className="w-full"
                />
              );
            })}
            <div className="flex w-full">
              <OffenderTypeTextField
                variableType={`familyMembers[${lastMemberIndex}].identifierType`}
                variableTypes={individualProfileTypes}
                variableName={`familyMembers.${lastMemberIndex}.idNumber`}
                values={values}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                required
                className="mb-2 w-full"
                containerClassName="flex w-full"
                // Due to the form's flow, empty forms are removed at the end
                // which causes undefined error on placeholder. Need to revise at some point.
                placeholder={
                  OffenderIdentifierTypeDescription[
                    values.familyMembers[lastMemberIndex]?.identifierType ??
                      OffenderIdentifierType.SaIdNumber
                  ]
                }
              />
            </div>
          </div>
        );

        const _renderSubmittedFamilyMembers = () =>
          _.map(values.familyMembers, (familyMember, index) => {
            if (familyMember.firstName !== null && familyMember.firstName !== '') {
              return (
                <Card className="flex mt-1.5" key={crypto.randomUUID()}>
                  <div className="p-2">
                    <img src="assets/icons/id-card-icon.png" alt="companyReg" />
                  </div>
                  <div className="text-left flex flex-col justify-center">
                    <p className="text-xs font-bold text-text-grey">
                      {familyMember.firstName} {familyMember.lastName}
                    </p>
                    {renderErrorMessage(index, 'name')}
                    <p className="text-xs font-thin text-text-grey">
                      {`${OffenderIdentifierTypeDescription[familyMember.identifierType]}: ${
                        familyMember.idNumber
                      }`}
                    </p>
                    {renderErrorMessage(index, 'id')}
                  </div>
                  <div className="flex flex-1" />
                  <IconButton onClick={_editMember(index)} className="m-1">
                    <Edit className="h-4 w-4" />
                  </IconButton>
                  <IconButton onClick={_removeMember(index)} className="m-1">
                    <HighlightOff className="h-4 w-4" />
                  </IconButton>
                </Card>
              );
            }

            return <div key={crypto.randomUUID()} />;
          });

        const _renderFriendsAndFamilyMembersForm = () => (
          <>
            <div className="overflow-y-auto">
              <div className="grid gap-2 mt-2">
                <OffenderTypeTextField
                  values={values}
                  icon={<img src="assets/icons/id-card-icon.png" alt="idNumber" />}
                  variableName="idNumber"
                  variableType="identifierType"
                  variableTypes={individualProfileTypes}
                  errors={errors}
                  touched={touched}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full mb-2"
                  placeholder={OffenderIdentifierTypeDescription[values.identifierType]}
                />
                <FormTextField
                  inputClassName="rounded-l-none border-l-none"
                  placeholder={`Confirm ${
                    OffenderIdentifierTypeDescription[values.identifierType]
                  }`}
                  values={values}
                  icon={<img src="assets/icons/id-card-icon.png" alt="confirm IdNumber" />}
                  variableName="confirmIdNumber"
                  errors={errors}
                  touched={touched}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className="w-full"
                />
                <div className="flex justify-center ">
                  <p className="w-full text-2xl text-text-grey text-center ">Friends & Family</p>
                </div>
                <div className="overflow-y-auto max-h-48"> {_renderSubmittedFamilyMembers()} </div>
                {_renderFamilyMembers()}
                <Button onClick={_addMember} className="text-secondary font-thin" type="text">
                  + Add Additional Members
                </Button>
              </div>
            </div>
          </>
        );
        return (
          <form
            onReset={handleReset}
            onSubmit={handleSubmit}
            className={`auth-form-body w-full text-center ${
              hasFriendsAndFamily ? 'h-formThird' : ''
            }`}
          >
            <div className="flex flex-col flex-1 w-4/5 overflow-y-auto">
              <p className="auth-heading">Your Details</p>
              {generalError && <FormHelperText error>{generalError}</FormHelperText>}
              {!hasFriendsAndFamily ? (
                <div className="grid gap-4 w-full">
                  <OffenderTypeTextField
                    values={values}
                    icon={<img src="assets/icons/id-card-icon.png" alt="idNumber" />}
                    variableName="idNumber"
                    variableType="identifierType"
                    variableTypes={individualProfileTypes}
                    errors={errors}
                    touched={touched}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full mb-2"
                    placeholder={OffenderIdentifierTypeDescription[values.identifierType]}
                  />

                  <FormTextField
                    inputClassName="rounded-l-none border-l-none"
                    placeholder={`Confirm ${
                      OffenderIdentifierTypeDescription[values.identifierType]
                    }`}
                    values={values}
                    icon={<img src="assets/icons/id-card-icon.png" alt="confirm IdNumber" />}
                    variableName="confirmIdNumber"
                    errors={errors}
                    touched={touched}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full"
                  />
                  <div className="flex flex-row">
                    <div className="flex flex-1" />
                    <Button
                      onClick={_addFriendsAndFamily}
                      className="text-secondary -mt-6"
                      type="text"
                    >
                      + Add Friends & Family
                    </Button>
                  </div>
                </div>
              ) : (
                <>{_renderFriendsAndFamilyMembersForm()} </>
              )}
              <div className="flex flex-1" />
              <Button
                isLoading={isSubmitting}
                onClick={handleSubmit}
                className="auth-form-submit-button mt-2"
              >
                Continue
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
